/** A data provider of Hunter. Tracxn is frozen at December 2025. */
export type ProviderSource = 'crunchbase' | 'sourcescrub' | 'tracxn';

/**
 * Where a value comes from: a provider, a manual change, Hunter itself or,
 * for subsectors, the classification of the company website.
 */
export type DataSource = ProviderSource | 'manual' | 'computed' | 'classified';

/** The providers in the priority of the unifier (COALESCE order). */
export const PROVIDERS: ProviderSource[] = [
  'crunchbase',
  'sourcescrub',
  'tracxn',
];

/** The short label of a source in its badge. */
export const SOURCE_ABBREVIATIONS: Record<DataSource, string> = {
  crunchbase: 'CB',
  sourcescrub: 'SS',
  tracxn: 'TX',
  manual: 'M',
  computed: 'Σ',
  classified: 'AI',
};

/** The decoded field sources of the API: field name to source. */
export type FieldSourceMap = Partial<
  Record<string, ProviderSource | 'computed'>
>;

/**
 * The source information of a row of the API (company or child row): the ids
 * of the providers that delivered the row, the manual overwrites and, once
 * the unifier writes them, the exact source per field. `field_sources` comes
 * decoded from the API (object) or as the stored string `"1:CS-T…"`.
 */
export interface SourceRow {
  crunchbase_id?: string | null;
  sourcescrub_id?: string | null;
  tracxn_id?: string | null;
  field_sources?: FieldSourceMap | string | null;
  /** Fields whose value comes from the fallback field (name from legal_name). */
  field_sources_fallback?: string[] | null;
  overwrite_reference?: Partial<Record<string, string | null>> | null;
}

/** The source of one value. */
export interface ValueSource {
  /** The exact source; null when only the delivering providers are known. */
  exact: DataSource | null;
  /** The providers that delivered the row. */
  available: ProviderSource[];
  /** The value comes from the fallback field of its source. */
  fallback: boolean;
  /** The overwrite of a manual change, for its details. */
  overwriteId: string | null;
}

/**
 * The fields of the position string per table, in the order of
 * TRACKED_FIELDS in data-schema (format version 1). Tables without a list
 * here are only read from the decoded object of the API.
 */
export const TRACKED_FIELDS: Record<string, string[]> = {
  companies: [
    'city',
    'country_code',
    'state_code',
    'postal_code',
    'founded_at',
    'name',
    'legal_name',
    'short_description',
    'long_description',
    'ownership_type',
    'website_url',
    'domain',
    'linkedin_url',
    'twitter_url',
    'facebook_url',
    'icon',
    'parent_id',
    'similar_companies',
  ],
  employee_details: ['count', 'employee_range', 'growth_rate', 'date'],
  investments: [
    'amount',
    'currency_code',
    'date',
    'type',
    'post_money_evaluation',
  ],
};

const CODES: Record<string, ProviderSource | 'computed'> = {
  C: 'crunchbase',
  S: 'sourcescrub',
  T: 'tracxn',
  H: 'computed',
};

/**
 * Decodes the stored position string ("1:CS-…", one character per tracked
 * field: C/S/T provider, c/s/t provider through the fallback field, H
 * computed, - no value). Null for an unknown version or table.
 */
export const decodeFieldSources = (
  encoded: string,
  table: string
): { sources: FieldSourceMap; fallback: string[] } | null => {
  const match = /^1:(.*)$/.exec(encoded);
  const fields = TRACKED_FIELDS[table];
  if (!match || !fields) {
    return null;
  }
  const sources: FieldSourceMap = {};
  const fallback: string[] = [];
  [...match[1]].forEach((code, index) => {
    const field = fields[index];
    const source = CODES[code.toUpperCase()];
    if (field && source) {
      sources[field] = source;
      if (code !== code.toUpperCase()) {
        fallback.push(field);
      }
    }
  });
  return { sources, fallback };
};

const present = (id: string | null | undefined) =>
  id !== null && id !== undefined && String(id).trim() !== '';

/** The providers that delivered a row, in priority order. */
export const rowProviders = (row: SourceRow | null | undefined) =>
  row ? PROVIDERS.filter((provider) => present(row[`${provider}_id`])) : [];

/**
 * The source of a field of a row: a manual overwrite first, then the exact
 * source from `field_sources`, else the one provider of the row; with several
 * providers and no field sources the exact source is unknown.
 */
export const valueSource = (
  row: SourceRow | null | undefined,
  field: string,
  table = 'companies'
): ValueSource => {
  const available = rowProviders(row);
  const overwriteId = row?.overwrite_reference?.[field] || null;
  if (overwriteId) {
    return { exact: 'manual', available, fallback: false, overwriteId };
  }
  const decoded =
    typeof row?.field_sources === 'string'
      ? decodeFieldSources(row.field_sources, table)
      : row?.field_sources
        ? {
            sources: row.field_sources,
            fallback: row.field_sources_fallback || [],
          }
        : null;
  if (decoded && field in decoded.sources) {
    return {
      exact: decoded.sources[field] ?? null,
      available,
      fallback: decoded.fallback.includes(field),
      overwriteId: null,
    };
  }
  return {
    exact: available.length === 1 ? available[0] : null,
    available,
    fallback: false,
    overwriteId: null,
  };
};

/** A value Hunter computes itself (scores, recalculated growth). */
export const computedSource = (
  row: SourceRow | null | undefined
): ValueSource => ({
  exact: 'computed',
  available: rowProviders(row),
  fallback: false,
  overwriteId: null,
});

/** The source fields of an API row, for the view models. */
export const pickSources = (row: SourceRow | null | undefined): SourceRow => ({
  crunchbase_id: row?.crunchbase_id ?? null,
  sourcescrub_id: row?.sourcescrub_id ?? null,
  tracxn_id: row?.tracxn_id ?? null,
  field_sources: row?.field_sources ?? null,
  field_sources_fallback: row?.field_sources_fallback ?? null,
  overwrite_reference: row?.overwrite_reference ?? null,
});

/**
 * The origin of a subsector: Tracxn rows carry the Tracxn taxonomy, rows of
 * Crunchbase or SourceScrub companies come from the classification of the
 * website (classification collector).
 */
export const subsectorSource = (
  row: SourceRow | null | undefined
): ValueSource => {
  const available = rowProviders(row);
  const overwriteId = row?.overwrite_reference?.['subsector_id'] || null;
  return {
    exact: overwriteId
      ? 'manual'
      : available.includes('tracxn')
        ? 'tracxn'
        : available.length
          ? 'classified'
          : null,
    available,
    fallback: false,
    overwriteId,
  };
};
